/* global HSCore */

import React, { useContext, useEffect, useRef, useState } from "react";
import LanguageContext from "../utils/Contexts/LanguageContext";
import { Country, State } from "country-state-city";
import { startCase } from "lodash";
import axios from "axios";
import toast from "react-hot-toast";
import axiosInstance from "../utils/Requests/axios";

function AddCardModal() {
  const { t } = useContext(LanguageContext);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [errors, setErrors] = useState({});
  const [cartPaymentMethods, setCartPaymentMethods] = useState(null);

  const [formValues, setFormValues] = useState({
    cardNumber: "",
    nameOnCard: "",
    expirationDate: "",
    securityCode: "",
    address: "",
    optionalAddress: "",
    city: "",
    state: "",
    zip: "",
    country: "",
    isPrimary: false,
    saveCard: false,
  });

  const handleCardDetails = (e) => {
    const { name, value, type, checked } = e.target;

    setFormValues({
      ...formValues,
      [name]: type === "checkbox" ? checked : value,
    });

    if (name === "cardNumber") {
      const sanitizedValue = value.replace(/\D/g, "");
      let formattedValue = sanitizedValue.replace(/(\d{4})(?=\d)/g, "$1-");

      if (formattedValue.length > 19) {
        formattedValue = formattedValue.slice(0, 19);
      }
      setFormValues((prev) => ({
        ...prev,
        [name]: formattedValue,
      }));
    }

    if (name === "nameOnCard") {
      const sanitizedValue = value.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ' -]/g, "");
      setFormValues((prev) => ({
        ...prev,
        [name]: sanitizedValue,
      }));
    }

    if (name === "expirationDate") {
      let sanitizedValue = value.replace(/[^0-9]/g, "");
      let formattedValue = "";

      if (sanitizedValue.length === 1) {
        if (sanitizedValue > "1") {
          formattedValue = `0${sanitizedValue}/`;
        } else {
          formattedValue = sanitizedValue;
        }
      } else if (sanitizedValue.length === 2) {
        const month = sanitizedValue.slice(0, 2);
        if (Number(month) > 12) {
          formattedValue = "12/";
        } else {
          formattedValue = `${month}/`;
        }
      } else if (sanitizedValue.length > 2) {
        const month = sanitizedValue.slice(0, 2);
        const year = sanitizedValue.slice(2, 4);

        if (Number(month) > 12) {
          formattedValue = "12/";
        } else {
          formattedValue = `${month}/${year}`;
        }
      }

      if (formattedValue.length > 5) {
        formattedValue = formattedValue.slice(0, 5);
      }

      // Update form values with formatted value
      setFormValues((prev) => ({
        ...prev,
        [name]: formattedValue,
      }));
    }

    if (name === "securityCode") {
      let sanitizedValue = value.replace(/[^0-9]/g, "");

      if (sanitizedValue.length > 4) {
        sanitizedValue = sanitizedValue.slice(0, 4);
      }

      setFormValues((prev) => ({
        ...prev,
        [name]: sanitizedValue,
      }));
    }

    if (name === "address") {
      const sanitizedValue = value.replace(
        /[^A-Za-z0-9À-ÖØ-öø-ÿ'.,#\-/ ]/g,
        ""
      );

      setFormValues((prev) => ({
        ...prev,
        [name]: sanitizedValue,
      }));
    }

    if (name === "city") {
      const sanitizedValue = value.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ' -]/g, "");

      setFormValues((prev) => ({
        ...prev,
        [name]: sanitizedValue,
      }));
    }

    if (name === "state") {
      const sanitizedValue = value.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ- ]/g, "");

      setFormValues((prev) => ({
        ...prev,
        [name]: sanitizedValue,
      }));
    }

    if (name === "zip") {
      const sanitizedValue = value.replace(/[^A-Za-z0-9 -]/g, "");

      setFormValues((prev) => ({
        ...prev,
        [name]: sanitizedValue,
      }));
    }
  };

  const validateForm = (formValues) => {
    let isValid = true;
    const localErrors = {};

    const fieldValidators = {
      // Matches a 16-digit card number, optionally formatted with spaces or hyphens
      cardNumber: /^(?:\d{16}|\d{4}([ -])\d{4}\1\d{4}\1\d{4})$/,

      // Matches expiration date in MM/YY format
      expirationDate: /^(0[1-9]|1[0-2])\/\d{2}$/,

      // Matches a name with at least two words, allowing accents, apostrophes, and hyphens
      nameOnCard:
        /^[A-Za-zÀ-ÖØ-öø-ÿ' -]{2,50}(?: [A-Za-zÀ-ÖØ-öø-ÿ' -]{2,50})+$/,

      // Matches a 3 or 4-digit CVV (for Visa, Mastercard, Amex)
      securityCode: /^\d{3,4}$/,

      // Matches any non-empty address (should be more refined for specific needs)
      address: /^[A-Za-z0-9À-ÖØ-öø-ÿ'.,#\-/ ]{5,100}$/,

      // Matches a city name (letters, spaces, apostrophes, and hyphens, 2 to 50 chars)
      city: /^[A-Za-zÀ-ÖØ-öø-ÿ' -]{2,50}$/,

      // Matches a state name or abbreviation (letters, spaces, hyphens, 2 to 50 chars)
      state: /^[A-Za-zÀ-ÖØ-öø-ÿ' -]{2,50}$/,

      // Matches US and international ZIP/postal codes (4-10 digits, optional "-1234")
      zip: /^[A-Za-z0-9 -]{4,10}$/,

      // Matches any non-empty country name (letters, spaces, hyphens, 2 to 50 chars)
      country: /^[A-Za-zÀ-ÖØ-öø-ÿ' -]{2,50}$/,
    };

    const excludeFields = ["isPrimary", "saveCard", "optionalAddress"];

    for (const field of Object.keys(formValues)) {
      if (excludeFields.includes(field)) {
        continue;
      }

      const value = formValues[field];
      let errorMessage = "";
      if (!value) {
        errorMessage = `${startCase(field)} is required`;
        isValid = false;
      } else if (field === "expirationDate") {
        const expirationDateRegex = /^(0[1-9]|1[0-2])\/\d{2}$/;
        if (!expirationDateRegex.test(value)) {
          errorMessage = "Invalid expiry date. Use mm/yy.";
          isValid = false;
        } else {
          const [month, year] = value.split("/").map(Number);
          const currentDate = new Date();
          const currentYear = currentDate.getFullYear() % 100;
          const currentMonth = currentDate.getMonth() + 1;

          if (
            year < currentYear ||
            (year === currentYear && month < currentMonth)
          ) {
            errorMessage = "Expiration date must be in the future.";
            isValid = false;
          }
        }
      } else if (field === "country") {
        if (!selectedCountry) {
          errorMessage = "Invalid country.";
          isValid = false;
        }
      } else if (field === "state") {
        if (!selectedCountry) {
          errorMessage = "Please select a valid country first.";
          isValid = false;
        } else {
          const state = State.getStatesOfCountry(selectedCountry).find(
            (s) =>
              s.name.toLowerCase() === value.toLowerCase() ||
              s.isoCode.toLowerCase() === value.toLowerCase()
          );

          if (!state) {
            errorMessage = "Invalid state.";
            isValid = false;
          }
        }
      } else if (
        fieldValidators[field] &&
        !fieldValidators[field].test(value)
      ) {
        const errorMessages = {
          cardNumber: "Invalid card number format.",
          expirationDate: "Invalid expiry date. Use mm/yy.",
          securityCode: "Invalid security code.",
        };
        errorMessage =
          errorMessages[field] ||
          `${"Invalid"} ${field.replace(/([A-Z])/g, " $1").toLowerCase()}.`;
        isValid = false;
      }

      if (errorMessage) {
        localErrors[field] = errorMessage;
      }
    }

    setErrors(localErrors);
    return isValid;
  };

  useEffect(() => {
    const countryList = Country.getAllCountries().map((c) => ({
      label: c.name,
      value: c.isoCode,
    }));
    setCountries(countryList);
  }, []);

  const countrySelectInitRef = useRef(null);
  useEffect(() => {
    if (!countrySelectInitRef.current && countries.length) {
      HSCore.components.HSTomSelect.init(".js-select-country");
      // eslint-disable-next-line react-hooks/exhaustive-deps
      countrySelectInitRef.current = true;
    }
  }, [countries]);

  const handleCountryChange = (e) => {
    const select = document.getElementById("js-select-country");
    const control = select.tomselect;
    if (control) {
      setSelectedCountry(e.target.value);
      setFormValues((prevValues) => ({
        ...prevValues,
        country: select.options[select.selectedIndex].text,
      }));
    }
  };

  const getConfig = () => {
    const url = `/cart/pay/config`;
    axiosInstance
      .get(url)
      .then((response) => {
        setCartPaymentMethods(response?.data?.methods);
      })
      .catch(() => {
        toast.error(t("There was an error fetching data"));
      });
  };

  useEffect(() => {
    getConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const stripeKey = cartPaymentMethods?.find(
    (method) => method.type === 2
  )?.key;

  const handleAddedCardPayment = async (e) => {
    e.preventDefault();
    const isFormValid = validateForm(formValues);
    if (!isFormValid) {
      toast.error(t("Please correct the errors before submitting."));
      return;
    }
    const [expMonth, expYear] = formValues?.expirationDate.split("/");
    try {
      await axios.post(
        "https://api.stripe.com/v1/tokens",
        new URLSearchParams({
          "card[name]": formValues.nameOnCard,
          "card[number]": formValues.cardNumber,
          "card[exp_month]": expMonth,
          "card[exp_year]": `20${expYear}`,
          "card[cvc]": formValues.securityCode,
          "card[address_line1]": formValues.address,
          "card[address_line2]": formValues.optionalAddress,
          "card[address_country]": formValues.country,
          "card[address_state]": formValues.state,
          "card[address_city]": formValues.city,
          "card[address_zip]": formValues.zip,
        }),
        {
          headers: {
            Authorization: `Bearer ${stripeKey}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );
    } catch (error) {
      console.error(
        "Error:",
        error.response ? error.response.data : error.message
      );
      toast.error(t("Adding card process failed."));
    }
  };

  return (
    <div
      id="modalAddCard"
      className="modal fade modalAddCard"
      tabIndex="-1"
      role="dialog"
      aria-labelledby="modalAddCardTitle"
      aria-hidden="true"
    >
      <div className="modal-dialog px-0" role="document">
        <div className="modal-content">
          <div className="modal-header px-4">
            <div className="w-100 border-bottom border-2 pb-2">
              <h3
                className="modal-title responsive-title"
                id="modalAddCardTitle"
              >
                {t("Add New Card")}
              </h3>
            </div>
          </div>
          <div className="modal-body mt-0 pt-0 px-4">
            <div className="row">
              {/* Card Details Section */}
              <div className="col-lg-6">
                <div className="py-3 text-dark">
                  <h5 className="responsive-paragraph mb-3 border-bottom border-2 pb-2">
                    {t("Card Detail")}
                  </h5>
                  <div className="row">
                    <div className="mb-3 col-12 col-md-6 col-lg-12">
                      <input
                        type="text"
                        className="form-control text-dark custom-placeholder rounded-2"
                        placeholder={t("Card Number")}
                        name="cardNumber"
                        value={formValues.cardNumber}
                        onChange={handleCardDetails}
                      />
                      {errors.cardNumber && (
                        <div className="text-danger">
                          {t(errors.cardNumber)}
                        </div>
                      )}
                    </div>
                    <div className="mb-3 col-12 col-md-6 col-lg-12">
                      <input
                        type="text"
                        className="form-control text-dark custom-placeholder rounded-2"
                        placeholder={t("Name On Card")}
                        name="nameOnCard"
                        value={formValues.nameOnCard}
                        onChange={handleCardDetails}
                      />
                      {errors.nameOnCard && (
                        <div className="text-danger">
                          {t(errors.nameOnCard)}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-6 mb-3">
                      <input
                        type="text"
                        className="form-control text-dark custom-placeholder rounded-2"
                        placeholder={t("Expiration Date")}
                        name="expirationDate"
                        value={formValues.expirationDate}
                        onChange={handleCardDetails}
                      />
                      {errors.expirationDate && (
                        <div className="text-danger">
                          {t(errors.expirationDate)}
                        </div>
                      )}
                    </div>
                    <div className="col-6 mb-3">
                      <input
                        type="number"
                        className="form-control text-dark custom-placeholder rounded-2"
                        placeholder={t("Security Code")}
                        name="securityCode"
                        value={formValues.securityCode}
                        onChange={handleCardDetails}
                      />
                      {errors.securityCode && (
                        <div className="text-danger">
                          {t(errors.securityCode)}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="form-check mb-3 d-none d-lg-block">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="primaryCard"
                      name="isPrimary"
                      checked={formValues.isPrimary}
                      onChange={handleCardDetails}
                    />
                    <label className="form-check-label" htmlFor="primaryCard">
                      {t("Make this primary card")}
                    </label>
                  </div>
                  <div className="form-check d-none d-lg-block">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="saveCard"
                      name="saveCard"
                      checked={formValues.saveCard}
                      onChange={handleCardDetails}
                    />
                    <label className="form-check-label" htmlFor="saveCard">
                      {t("Save my payment details for future purchases")}
                    </label>
                  </div>
                </div>
              </div>

              {/* Billing Address Section */}
              <div className="col-lg-6">
                <div className="py-3 text-dark">
                  <h5 className="responsive-paragraph mb-3 border-bottom border-2 pb-2">
                    {t("Billing Address")}
                  </h5>
                  <div className="row">
                    <div className="mb-3 col-12 col-md-6 col-lg-12">
                      <input
                        type="text"
                        className="form-control text-dark custom-placeholder rounded-2"
                        placeholder={t("Address")}
                        name="address"
                        value={formValues.address}
                        onChange={handleCardDetails}
                      />
                      {errors.address && (
                        <div className="text-danger">{t(errors.address)}</div>
                      )}
                    </div>
                    <div className="mb-3 col-12 col-md-6 col-lg-12">
                      <input
                        type="text"
                        className="form-control text-dark custom-placeholder rounded-2"
                        placeholder={t(
                          "Optional - Company, C/O, Apt, Suite, Unit"
                        )}
                        name="optionalAddress"
                        value={formValues.optionalAddress}
                        onChange={handleCardDetails}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-6 col-lg-5 mb-3">
                      <input
                        type="text"
                        className="form-control text-dark custom-placeholder rounded-2"
                        placeholder={t("City / Town")}
                        name="city"
                        value={formValues.city}
                        onChange={handleCardDetails}
                      />
                      {errors.city && (
                        <div className="text-danger">{t(errors.city)}</div>
                      )}
                    </div>
                    <div className="col-6 col-lg-4 mb-3">
                      <input
                        type="text"
                        className="form-control text-dark custom-placeholder rounded-2"
                        placeholder={t("State / Province")}
                        name="state"
                        value={formValues.state}
                        onChange={handleCardDetails}
                      />
                      {errors.state && (
                        <div className="text-danger">{t(errors.state)}</div>
                      )}
                    </div>
                    <div className="d-none d-lg-block col-lg-3 mb-3">
                      <input
                        type="number"
                        className="form-control text-dark custom-placeholder rounded-2"
                        placeholder={t("ZIP")}
                        name="zip"
                        value={formValues.zip}
                        onChange={handleCardDetails}
                      />
                      {errors.zip && (
                        <div className="text-danger">{t(errors.zip)}</div>
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="d-block d-lg-none col-6 mb-3">
                      <input
                        type="number"
                        className="form-control text-dark custom-placeholder rounded-2"
                        placeholder={t("ZIP")}
                        name="zip"
                        value={formValues.zip}
                        onChange={handleCardDetails}
                      />
                      {errors.zip && (
                        <div className="text-danger">{t(errors.zip)}</div>
                      )}
                    </div>
                    <div className="tom-select-custom col-6">
                      <select
                        id="js-select-country"
                        className="js-select-country form-select"
                        autoComplete="off"
                        data-hs-tom-select-options={JSON.stringify({
                          placeholder: t("Country"),
                          maxOptions: 250,
                        })}
                        onChange={handleCountryChange}
                      >
                        <option value="">{t("Country")}</option>
                        {countries.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    {errors.country && (
                      <div className="text-danger">{t(errors.country)}</div>
                    )}
                  </div>
                  <div className="form-check mb-3 d-block d-lg-none">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="primaryCard"
                      name="isPrimary"
                      checked={formValues.isPrimary}
                      onChange={handleCardDetails}
                    />
                    <label className="form-check-label" htmlFor="primaryCard">
                      {t("Make this primary card")}
                    </label>
                  </div>
                  <div className="form-check d-block d-lg-none">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="saveCard"
                      name="saveCard"
                      checked={formValues.saveCard}
                      onChange={handleCardDetails}
                    />
                    <label className="form-check-label" htmlFor="saveCard">
                      {t("Save my payment details for future purchases")}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-white"
              data-bs-dismiss="modal"
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleAddedCardPayment}
            >
              {t("Save")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddCardModal;
